// eslint-disable-next-line import/no-cycle
/* eslint-disable @typescript-eslint/naming-convention */

import { ClassGroupsSummary } from '@pages/admin/class-groups/types';
import { Challenge, ChallengeMeta, User } from '../redux/prop-types';
// eslint-disable-next-line import/no-cycle
import { AccessLevel } from './enums/access-levels';
import { isUserNotEnrolled } from './placement-test';
import {
  buildWhatsappUrl,
  getSupportDiscordUrl,
  getSupportInfoText,
  getSupportWhatsappNumber
} from './sctec-config';
import {
  isBlockedAccessAllowed,
  isBlockedByExpiration,
  isCurriculumAllowed,
  isInitialChallengeAllowed,
  isLimitedNacionalAccessAllowed,
  isModularAccessAllowed,
  isPreviousChallengeCompleted,
  isTrilha1AccessAllowed
} from './verify-access';

export function isUserAccessAllowed({
  challengeMeta,
  completedChallengeIds,
  challenge,
  user,
  challengeIndexForSuperBlock,
  isUserRegistrationConfirmed
}: {
  challengeMeta: ChallengeMeta;
  completedChallengeIds: string[];
  challenge: Challenge;
  user: User;
  challengeIndexForSuperBlock: number;
  isUserRegistrationConfirmed?: boolean;
}): boolean {
  const challengeId = challenge.id;

  // Regras globais de bloqueio
  if (isBlockedByExpiration(user)) return false;
  if (isBlockedAccessAllowed(user, challengeId, completedChallengeIds))
    return false;

  // Aluno sem turma querendo passar do MundoTech
  if (isUserNotEnrolled(user) && challenge.superOrder >= 1) return false;

  // Regras específicas de acesso MODULAR
  if (
    user.access === AccessLevel.MODULAR ||
    user.access === AccessLevel.LIMITED
  ) {
    const hasModularAccess = isModularAccessAllowed(
      user,
      challengeMeta,
      challengeIndexForSuperBlock
    );
    if (hasModularAccess !== null) return hasModularAccess;
  } else {
    // Acesso não modular (trilhas)

    if (isInitialChallengeAllowed(user, challenge)) return true;
    if (!isCurriculumAllowed(user, challengeMeta)) return false;
    if (!isTrilha1AccessAllowed(user, challenge)) return false;
    if (
      !isLimitedNacionalAccessAllowed(
        user,
        challenge,
        isUserRegistrationConfirmed
      )
    )
      return false;

    // Verificação final: desafio anterior completo
    return isPreviousChallengeCompleted(challengeMeta, completedChallengeIds);
  }

  return false;
}

export function formatCPF(cpf: string): string {
  return cpf?.replace(/[.-]/g, '');
}

export function continueProgressAvailable(user: User) {
  if (user.registrationQuiz === 1) {
    if (user.isFundamentosDaProgramacaoFrontEndCert === true) {
      return true;
    }
    return false;
  }

  if (user.access === AccessLevel.BLOCKED || isUserNotEnrolled(user)) {
    return false;
  }

  return true;
}

export function isNacionalUser(userAccess: string): boolean {
  return userAccess === AccessLevel.COMPLETE_NACIONAL;
}

export function formatClassOption(
  source: string,
  classGroupSummary: ClassGroupsSummary[]
): string {
  const classGroupFiltered = classGroupSummary.filter(
    classGroup => classGroup.identifier === source
  );

  if (Array.isArray(classGroupFiltered)) {
    if (classGroupFiltered.length > 0) {
      return classGroupFiltered[0].name;
    }
  }
  return 'Sem turma';
}

export function replaceTrilhaWithModuloInHTML(htmlString: string) {
  // Cria um container temporário para manipular o DOM
  const container = document.createElement('div');
  container.innerHTML = htmlString;

  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    null,
    false
  );

  while (walker.nextNode()) {
    const node = walker.currentNode;

    node.nodeValue = node.nodeValue
      .replace(/\bnesta primeira trilha\b/gi, match =>
        match[0] === 'N' ? 'Neste primeiro módulo' : 'neste primeiro módulo'
      )
      .replace(/\bnesta trilha\b/gi, match =>
        match[0] === 'N' ? 'Neste módulo' : 'neste módulo'
      )
      .replace(/\bdesta trilha\b/gi, match =>
        match[0] === 'D' ? 'Deste módulo' : 'deste módulo'
      )
      .replace(/\bda trilha\b/gi, match =>
        match[0] === 'D' ? 'Do módulo' : 'do módulo'
      )
      .replace(/\ba trilha\b/gi, match =>
        match[0] === 'A' ? 'O módulo' : 'o módulo'
      )
      .replace(/\ba \[trilha\b/gi, match =>
        match[0] === 'A' ? 'O módulo' : 'o módulo'
      )
      .replace(/\bTrilha\b/g, 'Módulo')
      .replace(/\btrilha\b/g, 'módulo');
  }

  return container.innerHTML;
}

const DEVSTART_WHATSAPP_QUESTION_TEXT =
  'Olá! 👋 Estou usando o DEVstart e tenho uma dúvida. Pode me ajudar? Sobre: ';

/**
 * Ajusta o HTML dos desafios de acordo com o programa do usuário (DEVstart ou SCTEC):
 * - `<a data-support-link="discord|whatsapp">`: recebe o href do programa atual.
 * - `[data-program="devstart|sctec"]`: elementos de outro programa são removidos.
 */
export function applyProgramContentInHTML(
  htmlString: string,
  isSCTEC: boolean
): string {
  if (
    !htmlString.includes('data-support-link') &&
    !htmlString.includes('data-program')
  ) {
    return htmlString;
  }

  const container = document.createElement('div');
  container.innerHTML = htmlString;

  const whatsappText = isSCTEC
    ? getSupportInfoText(true, 'question')
    : DEVSTART_WHATSAPP_QUESTION_TEXT;

  const supportHrefs: Record<string, string> = {
    discord: getSupportDiscordUrl(isSCTEC),
    whatsapp: buildWhatsappUrl(
      getSupportWhatsappNumber(isSCTEC),
      encodeURIComponent(whatsappText)
    )
  };

  container
    .querySelectorAll<HTMLAnchorElement>('a[data-support-link]')
    .forEach(link => {
      const href = supportHrefs[link.dataset.supportLink ?? ''];
      if (href) {
        link.setAttribute('href', href);
        link.setAttribute('rel', 'noopener noreferrer');
      }
    });

  const currentProgram = isSCTEC ? 'sctec' : 'devstart';
  container.querySelectorAll<HTMLElement>('[data-program]').forEach(element => {
    if (element.dataset.program !== currentProgram) {
      element.remove();
    }
  });

  return container.innerHTML;
}

export function isModularUser(user: User): boolean {
  return user.access === 'modular' || user.access === 'limited';
}

export function getBlockNameTitle(
  user: User,
  superBlock: string,
  phase: string,
  block: string
) {
  const isModular = isModularUser(user);
  return isModular
    ? `${`intro:${superBlock}.phases.${phase}.blocks.${block}.titleModule`}`
    : `${`intro:${superBlock}.phases.${phase}.blocks.${block}.titleTrail`}`;
}
